import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Menu, X, LogOut, Wallet } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { useSidebar } from '@/hooks/useSidebar'
import { useAuthStore } from '@/lib/auth-store'
import { authService } from '@/services/auth.service'
import { apiService } from '@/services/api.service'
import { navigationConfig } from '@/config/navigation'
import { LogoutConfirmModal } from '@/components/auth/LogoutConfirmModal'
import { cn, formatCurrency } from '@/lib/utils'
import { Logo } from '@/components/ui/Logo'

export function SidebarMobile() {
  const location = useLocation()
  const { isMobileOpen, setMobileOpen } = useSidebar()
  const { user } = useAuthStore()
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [avatarError, setAvatarError] = useState(false)

  // Fetch balance from API (compartilha cache com Dashboard)
  const { data: balance } = useQuery({
    queryKey: ['balance'],
    queryFn: () => apiService.fetchBalance(),
    refetchInterval: 5 * 60 * 1000,
  })

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <>
      {/* Top Navbar - Mobile */}
      <div className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-background px-4 lg:hidden">
        <Link to="/dashboard" className="flex items-center">
          <Logo className="text-foreground" />
        </Link>

        <button
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center justify-center h-10 w-10 rounded-lg hover:bg-muted transition-colors"
        >
          <Menu className="h-6 w-6 text-muted-foreground" />
        </button>
      </div>

      {/* Drawer */}
      <Dialog.Root open={isMobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-background/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content
            className={cn(
              'fixed inset-y-0 left-0 z-50 w-[85vw] max-w-sm',
              'flex flex-col gap-4 border-r border-sidebar-border',
              'bg-sidebar/95 backdrop-blur-xl p-6 shadow-xl',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
              'duration-300',
            )}
          >
            <VisuallyHidden.Root>
              <Dialog.Title>Menu de navegação</Dialog.Title>
              <Dialog.Description>Navegue pelas seções do zonisigma.app</Dialog.Description>
            </VisuallyHidden.Root>

            {/* Header */}
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center" onClick={() => setMobileOpen(false)}>
                <Logo className="text-sidebar-foreground" />
              </Link>
              <Dialog.Close className="rounded-lg p-2 hover:bg-sidebar-accent/50 transition-colors">
                <X className="h-5 w-5 text-sidebar-foreground" />
              </Dialog.Close>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto">
              <div className="space-y-4">
                {navigationConfig.map((section, index) => (
                  <div key={section.name}>
                    {index > 0 && <div className="h-px bg-sidebar-border/30 my-3" />}
                    <p className="px-2.5 text-[10.5px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-2">
                      {section.name}
                    </p>
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const Icon = item.icon
                        const active = isActive(item.href)
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px]',
                              'transition-all duration-300',
                              active
                                ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold'
                                : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
                            )}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span>{item.name}</span>
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </nav>

            {/* Frase Motivacional */}
            <div className="px-2 py-3 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10">
              <p className="text-[10px] font-medium text-center leading-relaxed text-sidebar-foreground/60 uppercase tracking-wide">
                O mercado premia a consistência,
                <br />
                <span className="text-primary font-semibold">nunca desista!</span>
              </p>
            </div>

            {/* Saldo da Carteira */}
            <div className="px-2 py-3 rounded-xl bg-sidebar-accent/30 border border-sidebar-border/30">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="h-4 w-4 text-primary/70" />
                <span className="text-[10px] font-medium text-sidebar-foreground/50 uppercase tracking-wide">
                  Saldo Disponível
                </span>
              </div>
              <p className="text-lg font-bold text-primary">
                {formatCurrency(balance || 0)}
              </p>
            </div>

            {/* Footer - User Profile */}
            {user && (
              <div className="border-t border-sidebar-border/50 pt-4">
                <div className="flex items-center gap-3 rounded-lg bg-sidebar-accent/30 p-3">
                  {user.avatar && !avatarError ? (
                    <img
                      src={user.avatar}
                      alt={user.nickname || user.name || 'Avatar'}
                      className="h-10 w-10 rounded-full object-cover shrink-0"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary shrink-0">
                      {(user.nickname || user.name || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-medium text-sidebar-foreground">
                      {user.nickname || user.name}
                    </p>
                    <p className="truncate text-xs text-sidebar-foreground/70">{user.email}</p>
                  </div>
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="p-1.5 rounded-lg hover:bg-sidebar-accent text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
                    title="Sair"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <LogoutConfirmModal
        open={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        onConfirm={() => authService.logout()}
      />
    </>
  )
}
